import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import AuthLayout from '../components/AuthLayout';
const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const {
    login,
    isLoading
  } = useAuth();
  const navigate = useNavigate();
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }
    try {
      const success = await login(email, password, rememberMe);
      if (success) {
        navigate('/');
      } else {
        setError('Invalid email or password');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    }
  };
  return <AuthLayout title="Welcome Back" subtitle="Track all your subscriptions in one place" data-id="element-640">
      <div className="mt-8 space-y-6" data-id="element-641">
        <div data-id="element-642">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-643">
            Sign in to your account
          </h2>
          <p className="mt-2 text-sm text-gray-600" data-id="element-644">
            Or{' '}
            <Link to="/signup" className="font-medium text-teal-600 hover:text-teal-500 transition-colors" data-id="element-645">
              create a new account
            </Link>
          </p>
        </div>
        {error && <div className="p-4 rounded-md bg-red-50 text-red-700" data-id="element-646">{error}</div>}
        <form className="space-y-6" onSubmit={handleSubmit} data-id="element-647">
          <div className="space-y-4" data-id="element-648">
            <div data-id="element-649">
              <label htmlFor="email-address" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-650">
                Email address
              </label>
              <input id="email-address" name="email" type="email" autoComplete="email" required className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-teal-500 focus:border-teal-500 transition-all" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} data-id="element-651" />
            </div>
            <div data-id="element-652">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-653">
                Password
              </label>
              <div className="relative" data-id="element-654">
                <input id="password" name="password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" required className="appearance-none block w-full pr-10 px-4 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-teal-500 focus:border-teal-500 transition-all" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} data-id="element-655" />
                <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600" onClick={() => setShowPassword(!showPassword)} data-id="element-656">
                  {showPassword ? <EyeOffIcon className="h-5 w-5" data-id="element-657" /> : <EyeIcon className="h-5 w-5" data-id="element-658" />}
                </button>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between" data-id="element-659">
            <div className="flex items-center" data-id="element-660">
              <input id="remember-me" name="remember-me" type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded transition-all" data-id="element-661" />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700" data-id="element-662">
                Remember me
              </label>
            </div>
            <div className="text-sm" data-id="element-663">
              <Link to="/forgot-password" className="font-medium text-teal-600 hover:text-teal-500 transition-colors" data-id="element-664">
                Forgot your password?
              </Link>
            </div>
          </div>
          <div data-id="element-665">
            <button type="submit" disabled={isLoading} className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 transition-all disabled:opacity-70" data-id="element-666">
              {isLoading ? <span className="flex items-center" data-id="element-667">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-668">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-669"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-670"></path>
                  </svg>
                  Signing in...
                </span> : 'Sign in'}
            </button>
          </div>
        </form>
        <div data-id="element-671">
          <div className="relative" data-id="element-672">
            <div className="absolute inset-0 flex items-center" data-id="element-673">
              <div className="w-full border-t border-gray-300" data-id="element-674"></div>
            </div>
            <div className="relative flex justify-center text-sm" data-id="element-675">
              <span className="px-2 bg-white text-gray-500" data-id="element-676">
                Or continue with
              </span>
            </div>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-3" data-id="element-677">
            <button type="button" className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-all" data-id="element-678">
              <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="currentColor" data-id="element-679">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" data-id="element-680" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" data-id="element-681" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" data-id="element-682" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" data-id="element-683" />
              </svg>
              Google
            </button>
            <button type="button" className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-all" data-id="element-684">
              <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20" data-id="element-685">
                <path d="M6.29 18.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0020 3.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.073 4.073 0 01.8 7.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 010 16.407a11.616 11.616 0 006.29 1.84" data-id="element-686" />
              </svg>
              Twitter
            </button>
          </div>
        </div>
      </div>
    </AuthLayout>;
};
export default Login;